import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, GraduationCap, Map, Star, AlertTriangle, Plus, Activity as ActivityIcon,
  ClipboardCheck, CalendarClock, TrendingUp, RefreshCw,
} from "lucide-react";
import { api, ukDate, initials, avatarColor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Donut } from "@/components/Donut";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EmployeeForm from "@/components/EmployeeForm";

function StatCard({ testid, icon: Icon, label, value, sub, donut, onClick }) {
  return (
    <div data-testid={testid} onClick={onClick} className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md ${onClick ? "cursor-pointer hover:-translate-y-0.5" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 text-slate-700">
            <Icon className="h-5 w-5" />
          </div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 font-heading text-3xl font-extrabold text-slate-900">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        {donut != null && <Donut value={donut} size={64} />}
      </div>
    </div>
  );
}

function Card({ title, action, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rag, setRag] = useState(null);

  const load = () => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/compliance/rag").then((r) => setRag(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const syncEleap = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/integrations/eleap/sync");
      toast.success(`Synced ${res.synced} employees (simulated)`);
      load();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  const firstName = user.name.split(" ")[0];
  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {greeting}, {firstName}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Here's what's happening across your team today.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-slate-500 md:block">{ukDate()}</span>
          <Button data-testid="add-employee-button" onClick={() => setFormOpen(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="mr-1.5 h-4 w-4" /> Add Employee
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard testid="kpi-total-employees" onClick={() => navigate("/people")} icon={Users} label={user.role === "admin" || user.role === "hr" ? "Total Employees" : "My Employees"} value={data.total_employees} sub={user.role === "rcsm" ? "allocated to you" : "across the organisation"} />
        <StatCard testid="kpi-pending-reviews" onClick={() => navigate("/reviews")} icon={Star} label="Reviews Due" value={data.reviews_due} sub="within 30 days" />
        <StatCard testid="kpi-expiring-training" onClick={() => navigate("/work")} icon={AlertTriangle} label="Overdue Items" value={data.overdue_items} sub="need attention" />
      </div>

      {/* Compliance by region (deep-links into the pre-filtered training matrix) */}
      {rag?.rows?.length > 0 && (
        <div data-testid="dashboard-rag">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-900">Compliance by region</h2>
            <button data-testid="dashboard-rag-view-all" onClick={() => navigate("/training-matrix")} className="text-xs font-semibold text-orange-600 hover:text-orange-700">
              View training matrix →
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rag.rows.map((g) => (
              <button
                key={g.region}
                data-testid="dashboard-rag-region"
                onClick={() => navigate(`/training-matrix?region=${encodeURIComponent(g.region_id)}`)}
                className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md"
              >
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-heading text-sm font-bold text-slate-800">{g.region}</p>
                  <span className="text-xs text-slate-400">{g.total} staff</span>
                </div>
                <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                  {g.green > 0 && <div style={{ width: `${(g.green / g.total) * 100}%` }} className="bg-emerald-500" />}
                  {g.amber > 0 && <div style={{ width: `${(g.amber / g.total) * 100}%` }} className="bg-amber-400" />}
                  {g.red > 0 && <div style={{ width: `${(g.red / g.total) * 100}%` }} className="bg-red-500" />}
                </div>
                <div className="mt-2 flex gap-3 text-xs">
                  <span className="text-emerald-600">{g.green} compliant</span>
                  <span className="text-amber-600">{g.amber} attention</span>
                  <span className="text-red-600">{g.red} at risk</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Role-specific */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {(user.role === "admin" || user.role === "hr") && (
          <StatCard testid="kpi-regions" onClick={() => navigate("/regions")} icon={Map} label="Regions" value={data.regions} sub="operational areas" />
        )}
        {user.role === "rcsm" && (
          <>
            <StatCard testid="kpi-cnat-work" icon={ClipboardCheck} label="CNAT Work Due" value={data.cnat_work_due} sub="tasks you allocated" />
            <StatCard testid="kpi-low-compliance" icon={GraduationCap} label="eLearning Below Target" value={data.low_compliance} sub="employees under 90%" />
            <Card title="KPI Monitor" testid="kpi-monitor-card">
              {data.kpi_monitor?.length ? (
                <ul className="space-y-2">
                  {data.kpi_monitor.map((k) => (
                    <li key={k.id} className="flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2">
                      <span className="text-sm font-medium text-amber-900">{k.name}</span>
                      <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">{k.supervisions} supervisions</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">No employees flagged for monitoring.</p>
              )}
            </Card>
          </>
        )}
        {user.role === "cnat" && (
          <>
            <StatCard testid="kpi-outstanding-training" icon={GraduationCap} label="Outstanding Training" value={data.outstanding_training} sub="employees under 90%" />
            <StatCard testid="kpi-new-tasks" icon={ClipboardCheck} label="New Tasks" value={data.new_tasks} sub="allocated by RCSM" />
            <StatCard testid="kpi-annual-assessments" icon={Star} label="Annual Assessments Due" value={data.annual_assessments_due} />
            <StatCard testid="kpi-field-visits" icon={CalendarClock} label="6-Monthly Field Visits" value={data.field_visits_due} sub="due" />
          </>
        )}
      </div>

      {/* Quick actions + activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card title="Recent Activity" testid="recent-activity-card">
          <ul className="space-y-3">
            {data.recent_activity?.length ? data.recent_activity.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <ActivityIcon className="h-3.5 w-3.5" />
                </div>
                <div className="text-sm">
                  <span className="font-medium text-slate-900">{a.actor_name}</span>{" "}
                  <span className="text-slate-500">{a.action}</span>{" "}
                  <span className="font-medium text-slate-700">{a.target}</span>
                </div>
              </li>
            )) : <p className="text-sm text-slate-400">No recent activity.</p>}
          </ul>
        </Card>

        <Card title="Quick Actions" testid="quick-actions-card">
          <div className="space-y-2">
            <QuickAction icon={Plus} label="Add a new employee" onClick={() => setFormOpen(true)} testid="qa-add-employee" />
            <QuickAction icon={Users} label="Browse People directory" onClick={() => navigate("/people")} testid="qa-people" />
            <QuickAction icon={ClipboardCheck} label="View work allocation" onClick={() => navigate("/work")} testid="qa-work" />
          </div>
        </Card>

        <Card title="Training Overview (eLeaP)" testid="training-overview-card" action={
          (user.role === "admin" || user.role === "hr") ? (
            <button data-testid="dashboard-eleap-sync" onClick={syncEleap} disabled={syncing} className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync eLeaP"}
            </button>
          ) : null
        }>
          <div className="flex items-center gap-5">
            <Donut value={data.training_completion} size={96} stroke={11} />
            <div className="text-sm">
              <p className="font-heading text-2xl font-extrabold text-slate-900">{data.training_completion}%</p>
              <p className="text-slate-500">Team completion</p>
              <p className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
                <TrendingUp className="h-3.5 w-3.5" /> Target 100%
              </p>
            </div>
          </div>
        </Card>
      </div>

      <EmployeeForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-orange-300 hover:bg-orange-50"
    >
      <Icon className="h-4 w-4 text-orange-500" /> {label}
    </button>
  );
}
